// Offline manager: downloads (IndexedDB blobs, version-keyed + byte-verified),
// local-first progress, generic idempotent outbox, and library metadata cache.
import { idbGet, idbAll, idbPut, idbDel, metaGet, metaSet, idbClear } from "./listenDB";
import { audioUrl, coverUrl, listenFetch, listenFetchForm } from "./listenApi";

const audioKey = (relId, itemId, ver) => `${relId}|${itemId}|${ver}`;
const progKey = (relId, itemId) => `${relId}|${itemId}`;

export async function deviceId() {
  let id = await metaGet("device_id");
  if (!id) { id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()); await metaSet("device_id", id); }
  return id;
}

// ---- Downloads -------------------------------------------------------------
export async function downloadItem(relId, item, token, onProgress) {
  const url = audioUrl(item.item_id, token);
  const res = await fetch(url, { headers: { "X-Listen-Session": token } });
  if (!res.ok) throw new Error(res.status === 403 ? "auth" : "network");
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!(ct.includes("audio") || ct.includes("mpeg") || ct.includes("octet-stream")))
    throw new Error("not-audio");
  const expected = parseInt(res.headers.get("content-length") || "0", 10);
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error("empty");
  if (expected && Math.abs(blob.size - expected) > 16) throw new Error("incomplete");
  if (onProgress) onProgress(blob.size);
  await idbPut("audio", {
    key: audioKey(relId, item.item_id, item.audio_version), release_id: relId,
    item_id: item.item_id, audio_version: item.audio_version, bytes: blob.size,
    blob, downloaded_at: Date.now(),
  });
  // Drop superseded versions of the same item.
  const all = await idbAll("audio");
  for (const a of all) {
    if (a.release_id === relId && a.item_id === item.item_id && a.audio_version !== item.audio_version)
      await idbDel("audio", a.key);
  }
  return blob.size;
}

export async function removeDownload(relId, item) {
  const all = await idbAll("audio");
  for (const a of all) if (a.release_id === relId && a.item_id === item.item_id) await idbDel("audio", a.key);
}

export async function removeAllDownloads(relId) {
  const all = await idbAll("audio");
  for (const a of all) if (!relId || a.release_id === relId) await idbDel("audio", a.key);
}

// Returns per-item state: 'downloaded' | 'outdated' | 'none'. Reflects the blob
// actually existing (repairs UI if iOS evicted storage).
export async function downloadStates(relId, items) {
  const all = await idbAll("audio");
  const byItem = {};
  all.forEach((a) => { if (a.release_id === relId) (byItem[a.item_id] = byItem[a.item_id] || []).push(a); });
  const out = {};
  let totalBytes = 0;
  for (const it of items) {
    const entries = byItem[it.item_id] || [];
    const cur = entries.find((e) => e.audio_version === it.audio_version && e.blob && e.bytes > 0);
    if (cur) { out[it.item_id] = "downloaded"; totalBytes += cur.bytes; }
    else if (entries.length) out[it.item_id] = "outdated";
    else out[it.item_id] = "none";
  }
  return { states: out, totalBytes };
}

// True only if a byte-verified blob for this exact item+version is present.
export async function isDownloaded(relId, item) {
  const rec = await idbGet("audio", audioKey(relId, item.item_id, item.audio_version));
  return !!(rec && rec.blob && rec.bytes > 0);
}

export async function getAudioBlobUrl(relId, item) {
  const rec = await idbGet("audio", audioKey(relId, item.item_id, item.audio_version));
  if (rec && rec.blob && rec.bytes > 0) return URL.createObjectURL(rec.blob);
  return null;
}

export async function storageEstimate() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const e = await navigator.storage.estimate();
      return { usage: e.usage || 0, quota: e.quota || 0, available: (e.quota || 0) - (e.usage || 0) };
    }
  } catch { /* ignore */ }
  return null;
}

// ---- Cover art (cached in the meta store, version-keyed) -------------------
export async function cacheCover(token, version) {
  if (!version) return;
  const existing = await metaGet("cover_" + version);
  if (existing && existing.blob) return;
  try {
    const res = await fetch(coverUrl(token, version), { headers: { "X-Listen-Session": token } });
    if (!res.ok) return;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return;
    await metaSet("cover_" + version, { blob, version });
    const all = await idbAll("meta");
    for (const m of all) if (m.key.startsWith("cover_") && m.key !== "cover_" + version) await idbDel("meta", m.key);
  } catch { /* ignore */ }
}

export async function getCoverBlobUrl(version) {
  if (!version) return null;
  const rec = await metaGet("cover_" + version);
  if (rec && rec.blob) return URL.createObjectURL(rec.blob);
  return null;
}

// ---- Local-first progress + outbox ----------------------------------------
export async function localProgress(relId) {
  const all = await idbAll("progress");
  const map = {};
  all.forEach((p) => { if (p.release_id === relId) map[p.item_id] = p; });
  return map;
}

async function nextSeq() {
  const s = (await metaGet("seq", 0)) + 1;
  await metaSet("seq", s);
  return s;
}

// Records progress locally and enqueues a typed outbox event. Position is NOT
// monotonic locally (rewind allowed); completion never downgrades locally.
export async function recordProgress(relId, item, position, duration, completed) {
  const key = progKey(relId, item.item_id);
  const prev = await idbGet("progress", key);
  const comp = !!completed || !!(prev && prev.completed);
  const seq = await nextSeq();
  const now = new Date().toISOString();
  await idbPut("progress", {
    key, release_id: relId, item_id: item.item_id, position: Math.max(0, position || 0),
    duration: duration || item.duration || 0, completed: comp, updated_at: now, seq,
  });
  await metaSet("last_listen_" + relId, { item_id: item.item_id, position: Math.max(0, position || 0), updated_at: now });
  const id = (crypto.randomUUID && crypto.randomUUID()) || `${seq}-${Date.now()}`;
  await idbPut("outbox", {
    key: id, id, type: completed ? "release_item_completed" : "progress_updated", seq,
    payload: { release_id: relId, item_id: item.item_id, position: Math.max(0, position || 0), duration: duration || null, completed: comp },
    created_at: now,
  });
}

let _flushing = false;
export async function flushOutbox(token) {
  if (_flushing || !navigator.onLine) return { ok: false, pending: (await idbAll("outbox")).length };
  const events = await idbAll("outbox");
  if (!events.length) return { ok: true, pending: 0 };
  _flushing = true;
  try {
    const body = JSON.stringify({ events: events.map((e) => ({ id: e.id, type: e.type, seq: e.seq, payload: e.payload })) });
    const res = await listenFetch("/listen/sync", token, { method: "POST", body });
    const acked = new Set(res.acked || []);
    for (const e of events) if (acked.has(e.id)) await idbDel("outbox", e.key);
    const left = await idbAll("outbox");
    return { ok: true, pending: left.length };
  } catch (e) {
    return { ok: false, pending: events.length, error: e };
  } finally { _flushing = false; }
}

export const outboxCount = async () => (await idbAll("outbox")).length;

// ---- Feedback (timestamped notes) — local-first, offline-capable ----------
// Each note is stored locally with a client feedback_id (idempotent on the
// server). Voice notes keep their audio blob until synced. flushFeedback pushes
// unsynced notes and pulls the server's transcript status back.
export function newFeedbackId() {
  return (crypto.randomUUID && crypto.randomUUID()) || `fb-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export async function queueTextFeedback({ feedbackId, itemId, itemTitle, position, text }) {
  const now = new Date().toISOString();
  await idbPut("feedback", {
    key: feedbackId, feedback_id: feedbackId, item_id: itemId, item_title: itemTitle,
    position: position || 0, kind: "text", text, transcript_status: "n/a",
    status: "active", synced: false, created_at: now, updated_at: now,
  });
}

export async function queueVoiceFeedback({ feedbackId, itemId, itemTitle, position, blob, contentType }) {
  const now = new Date().toISOString();
  await idbPut("feedback", {
    key: feedbackId, feedback_id: feedbackId, item_id: itemId, item_title: itemTitle,
    position: position || 0, kind: "voice", text: "", blob, content_type: contentType || "audio/webm",
    transcript_status: "queued", status: "active", synced: false, created_at: now, updated_at: now,
  });
}

export async function allFeedback() {
  const rows = await idbAll("feedback");
  return rows.filter((r) => r.status !== "discarded").sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
}

export async function feedbackForItem(itemId) {
  return (await allFeedback()).filter((r) => r.item_id === itemId);
}

export async function discardFeedback(feedbackId, token) {
  const rec = await idbGet("feedback", feedbackId);
  if (!rec) return;
  if (rec.synced && token && navigator.onLine) {
    try { await listenFetch(`/listen/feedback/${feedbackId}`, token, { method: "PATCH", body: JSON.stringify({ discard: true }) }); } catch { /* retry later */ }
  }
  await idbDel("feedback", feedbackId);
}

export async function editTextFeedback(feedbackId, text, token) {
  const rec = await idbGet("feedback", feedbackId);
  if (!rec) return;
  rec.text = text; rec.updated_at = new Date().toISOString(); rec.synced = false;
  await idbPut("feedback", rec);
  if (token && navigator.onLine) await flushFeedback(token);
}

let _fbFlushing = false;
export async function flushFeedback(token) {
  if (_fbFlushing || !navigator.onLine || !token) return { ok: false };
  _fbFlushing = true;
  try {
    const rows = await idbAll("feedback");
    for (const r of rows) {
      if (r.synced || r.status === "discarded") continue;
      try {
        if (r.kind === "voice" && r.blob) {
          const fd = new FormData();
          fd.append("feedback_id", r.feedback_id);
          fd.append("item_id", r.item_id);
          fd.append("position", String(r.position || 0));
          fd.append("client_created_at", r.created_at || "");
          const ext = (r.content_type || "").includes("mp4") ? "m4a" : "webm";
          fd.append("file", r.blob, `note.${ext}`);
          await listenFetchForm("/listen/feedback/voice", token, fd);
        } else if (r.kind === "text") {
          await listenFetch("/listen/feedback", token, { method: "POST", body: JSON.stringify({
            feedback_id: r.feedback_id, item_id: r.item_id, position: r.position || 0, text: r.text || "" }) });
        }
        r.synced = true;
        await idbPut("feedback", r);
      } catch { /* leave unsynced, retry next flush */ }
    }
    // Pull server transcript status back into local records.
    try {
      const server = await listenFetch("/listen/feedback", token);
      for (const s of (server.feedback || [])) {
        const rec = await idbGet("feedback", s.feedback_id);
        if (rec) {
          rec.transcript_status = s.transcript_status;
          if (s.kind === "voice" && s.text) rec.text = s.text;
          rec.synced = true;
          await idbPut("feedback", rec);
        }
      }
    } catch { /* ignore */ }
    return { ok: true };
  } finally { _fbFlushing = false; }
}

export const pendingFeedbackCount = async () =>
  (await idbAll("feedback")).filter((r) => !r.synced && r.status !== "discarded").length;

// ---- Library snapshot cache (offline shell) --------------------------------
export const cacheLibrary = (relId, data) => metaSet("library_" + relId, data);
export const cachedLibrary = (relId) => metaGet("library_" + relId, null);
export const cacheActiveRelease = (relId) => metaSet("active_release", relId);
export const activeRelease = () => metaGet("active_release", null);
export const wipeProgress = () => idbClear("progress");

// Continue pointer computed locally (mirrors server _compute_continue).
export function computeContinue(items, prog, lastListen) {
  if (!items.length) return null;
  const ids = items.map((i) => i.item_id);
  const hit = (it, pos) => ({ item_id: it.item_id, title: it.title, kind: it.kind, position: Math.round((pos || 0) * 100) / 100, duration: it.duration });
  if (lastListen && ids.includes(lastListen.item_id)) {
    const it = items.find((i) => i.item_id === lastListen.item_id);
    const p = prog[it.item_id] || {};
    if (!p.completed) return hit(it, p.position != null ? p.position : lastListen.position);
    const idx = ids.indexOf(it.item_id);
    for (const nxt of items.slice(idx + 1)) if (!(prog[nxt.item_id] || {}).completed) return hit(nxt, (prog[nxt.item_id] || {}).position || 0);
  }
  for (const it of items) if (!(prog[it.item_id] || {}).completed) return hit(it, (prog[it.item_id] || {}).position || 0);
  return hit(items[items.length - 1], (prog[items[items.length - 1].item_id] || {}).position || 0);
}
